import React, { useState, useCallback, useEffect } from "react"
import { useDropzone } from "react-dropzone"
import { Box, Stack, InputLabel, Typography, Avatar, Modal, Slider, Button, Switch } from "@mui/material";
import { UploadFile, Delete } from "@mui/icons-material";
import Cropper from "react-easy-crop";
import { readFile } from "../../../Helpers/ImageHelper";
import getCroppedImg from "../ImageInput/CropImage";


const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4
}


export default function AvatarDropzone({ title, avatarUrl = null, callbackFile }) {

  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [isApplyMonochromatic, setIsApplyMonochromatic] = useState(false)
  const [imageSrc, setImageSrc] = useState(null)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [file, setFile] = useState(null)

  useEffect(() => {
    if (avatarUrl !== null) {
      setFile(avatarUrl)
    }
  }, [avatarUrl])

  const onFileChange = useCallback(async (file) => {
    let imageDataUrl = await readFile(file)
    setImageSrc(imageDataUrl)
    handleOpen()
  }, [])

  const onDrop = useCallback(acceptedFiles => {
    onFileChange(acceptedFiles[0])
  }, [onFileChange])

  const showCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, isApplyMonochromatic)
      let imageDataUrl = await readFile(croppedImage)
      setFile(imageDataUrl)
      callbackFile(imageDataUrl)
    } catch (e) {
      console.error(e)
    }
  }

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }


  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const inputView =
    <>
      <Box
        {...getRootProps()}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
          border: "1px solid",
          color: "grey.500",
          borderRadius: "6px",
          '&:hover': {
            color: 'primary.main'
          },
        }}
      >
        <input {...getInputProps()} />
        {
          <>
            <UploadFile />
            <Typography
              variant="body1"
              marginLeft={2}
            >
              {"Click to choose a file or drag here"}
            </Typography>
          </>
        }
      </Box>

      <Modal
        open={open}
      >
        <Box
          sx={style}
        >
          <Typography
            variant="body1"
          >
            Zoom
          </Typography>

          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom)}
          />

          <Box
            sx={{
              marginTop: 2,
              position: "relative",
              width: "200px",
              height: "200px"
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={4 / 4}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </Box>

          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 2
            }}
          >
            <Typography
              variant="body1"
            >
              Apply Monochromatic
            </Typography>
            <Switch
              checked={isApplyMonochromatic}
              onChange={(event) => setIsApplyMonochromatic(event.target.checked)}
            />
          </Box>

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Button
              color="error"
              onClick={() => handleClose()}
            >
              Close
            </Button>
            <Button
              onClick={showCroppedImage}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Modal>
    </>


  return (
    <Stack
      spacing={1}
      marginTop={4}
    >
      <InputLabel>{title}</InputLabel>
      {file === null ? inputView : <SelectedView src={file} deleteCallback={() => { setFile(null) }} />}
    </Stack>
  )
}


function SelectedView({ src, deleteCallback }) {

  const [innerSrc] = useState(src)

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 2,
        border: "1px dashed",
        color: "grey.500",
        borderRadius: "6px",
        '&:hover': {
          color: 'primary.main'
        },
      }}
    >
      <Avatar
        variant="circular"
        src={innerSrc}
        sx={{ width: 68, height: 68 }}
      />
      <Delete
        onClick={() => deleteCallback()}
      />
    </Box>
  )
}
